import React, { forwardRef, useEffect, useRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import { getClassName } from 'utils/ui/classes';
import DotsVerticalIcon from 'components/v2/icons/DotsVerticalIcon';
import { useFloating, autoUpdate, limitShift } from '@floating-ui/react';
import { shift } from '@floating-ui/dom';
import classNames from 'classnames';
const baseClassName = 'inline-flex items-center justify-center w-full px-4 py-2 font-medium rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-200 focus:ring-green-cm';
const baseWideClassName = 'inline-flex items-center justify-center w-full px-8 py-3 font-semibold rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-200 focus:ring-green-cm';
const determineButtonClass = (wide) => {
    return wide ? baseWideClassName : baseClassName;
};
const DropdownMenuButton = forwardRef(function DropdownMenuButton({ label, expanded, isDark, isWide, dropDownButtonWidth, disabled }, ref) {
    return (React.createElement(Menu.Button, { "data-testid": "MenuButton-81d20177", ref: ref, className: getClassName(determineButtonClass(isWide), {
            'bg-white': !isDark,
            'text-gray-300': isDark,
            'text-gray-700': !isDark,
            'border-gray-300': !isDark,
            border: !isDark,
            'opacity-75': disabled,
            'cursor-not-allowed': disabled,
        }), disabled: disabled, "aria-haspopup": "true", "aria-expanded": expanded },
        React.createElement("span", { "data-testid": "span-fa1533aa", className: dropDownButtonWidth }, label),
        React.createElement(ChevronDownIcon, { className: "w-4 h-4 ml-2 -mr-1" })));
});
const DropdownMenuIconButton = forwardRef(function DropdownMenuIconButton({ className }, ref) {
    return (React.createElement(Menu.Button, { "data-testid": "MenuButton-c9ca5db4", ref: ref, className: classNames('flex items-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-100', className) },
        React.createElement("span", { "data-testid": "span-ce655f84", className: "sr-only" }, "Open options"),
        React.createElement(DotsVerticalIcon, { className: "h-5 w-5", "aria-hidden": "true" })));
});
const DropdownRenderButton = forwardRef(function DropdownRenderButton({ button, disabled }, ref) {
    return (React.createElement(Menu.Button, { "data-testid": "MenuButton-b83a10fd", ref: ref, disabled: disabled }, button));
});
const DropdownMenu = ({ title, button, children, isDark, isWide, menuClosed, useIcon, dropDownButtonWidth, disabled, dataTestId = 'dataTestId-Menu-Dropdown', }) => {
    const { refs, floatingStyles, update } = useFloating({
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
        middleware: [shift({ crossAxis: true, limiter: limitShift() })],
    });
    const prevOpenRef = useRef();
    return (React.createElement(Menu, { "data-testid": dataTestId }, ({ open }) => {
        useEffect(() => {
            if (prevOpenRef.current && !open && menuClosed) {
                menuClosed();
            }
            prevOpenRef.current = open;
        }, [open, menuClosed]);
        return (React.createElement(React.Fragment, null,
            button && (React.createElement(DropdownRenderButton, { button: button, disabled: disabled, ref: refs.setReference })),
            useIcon && !button && (React.createElement(DropdownMenuIconButton, { ref: refs.setReference })),
            !useIcon && !button && (React.createElement(DropdownMenuButton, { label: title, expanded: open, isDark: isDark, isWide: isWide, disabled: disabled, dropDownButtonWidth: dropDownButtonWidth, ref: refs.setReference })),
            React.createElement("div", { "data-testid": "div-1e2f2b28", ref: refs.setFloating, style: Object.assign(Object.assign({}, floatingStyles), { zIndex: 10000 }) },
                React.createElement(Transition, { show: open, enter: "transition ease-out duration-100", enterFrom: "transform opacity-0 scale-95", enterTo: "transform opacity-100 scale-100", leave: "transition ease-in duration-75", leaveFrom: "transform opacity-100 scale-100", leaveTo: "transform opacity-0 scale-95", beforeEnter: () => update(), beforeLeave: () => update() },
                    React.createElement(Menu.Items, { "data-testid": "undefined-d7430a81", static: true, className: "z-10 mt-2 bg-white shadow-lg outline-none rounded-md ring-black text-left" },
                        React.createElement("div", { "data-testid": "div-d018a67c", className: "py-1 dropdown z-10", role: "menu", "aria-orientation": "vertical", "aria-labelledby": "options-menu" }, children))))));
    }));
};
export default DropdownMenu;
